import { UseFormReturn } from "react-hook-form";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useState } from "react";
import { Upload, X, CheckCircle, Plus, Trash2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../ui/command";
import {
  INSTITUTION_TYPES,
  INSTITUTION_LEVELS,
  BOARDS,
  MEDIUM_OF_INSTRUCTION,
  DEPARTMENTS,
  FACILITIES,
  AFFILIATION_STATUS,
  ACCREDITATION,
  INDIAN_STATES,
  TRUSTEE_DESIGNATIONS,
  BOARDING_GENDER,
} from "../../constants/institutionFormFields";

const MAX_INSTITUTION_IMAGES_DEFAULT = 15;

export interface InstitutionOnboardingFormProps {
  step: number;
  form: UseFormReturn<any>;
  onNext: (data: any) => void;
  onBack: () => void;
  onFinalSubmit: (data: any) => void;
  selectedDepartments: string[];
  setSelectedDepartments: React.Dispatch<React.SetStateAction<string[]>>;
  selectedFacilities: string[];
  setSelectedFacilities: React.Dispatch<React.SetStateAction<string[]>>;
  selectedLevels: string[];
  setSelectedLevels: React.Dispatch<React.SetStateAction<string[]>>;
  institutionImages: File[];
  setInstitutionImages: React.Dispatch<React.SetStateAction<File[]>>;
  /** Existing image URLs from API (edit mode) — e.g. data.images array; user can remove */
  existingImageUrls?: string[];
  /** Called when user removes an existing image by index */
  onRemoveExistingImage?: (index: number) => void;
  isModal?: boolean;
  onCancel?: () => void;
  maxInstitutionImages?: number;
  isEditMode?: boolean;
}

function toggleInArray(
  setter: React.Dispatch<React.SetStateAction<string[]>>,
  value: string
) {
  setter(prev =>
    prev.includes(value) ? prev.filter(x => x !== value) : [...prev, value]
  );
}

export default function InstitutionOnboardingForm({
  step,
  form,
  onNext,
  onBack,
  onFinalSubmit,
  selectedDepartments,
  setSelectedDepartments,
  selectedFacilities,
  setSelectedFacilities,
  selectedLevels,
  setSelectedLevels,
  institutionImages,
  setInstitutionImages,
  existingImageUrls = [],
  onRemoveExistingImage,
  isModal = false,
  onCancel,
  maxInstitutionImages = MAX_INSTITUTION_IMAGES_DEFAULT,
  isEditMode = false,
}: InstitutionOnboardingFormProps) {
  const toggleDepartment = (dept: string) =>
    toggleInArray(setSelectedDepartments, dept);
  const toggleFacility = (facility: string) =>
    toggleInArray(setSelectedFacilities, facility);
  const toggleLevel = (level: string) =>
    toggleInArray(setSelectedLevels, level);

  // Step 3: draft row for educational year entries (educational_year, total_capacity, vacant, occupied)
  const [educationalYearDraft, setEducationalYearDraft] = useState({
    educational_year: "",
    total_capacity: "",
    vacant: "",
    occupied: "",
  });

  if (step === 1) {
    return (
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">Basic Information</h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>
                Institution Name <span className="text-destructive">*</span>
              </Label>
              <Input
                {...form.register("name", {
                  required: "Institution Name is required",
                })}
                placeholder="Enter institution name"
              />
              {form.formState.errors.name && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.name.message)}
                </p>
              )}
            </div>
            <div>
              <Label>Institution Type</Label>
              <Select
                onValueChange={(value: string) => form.setValue("type", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {INSTITUTION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Affiliation Status</Label>
              <Select
                onValueChange={(value: string) =>
                  form.setValue("affiliation_status", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select affiliation" />
                </SelectTrigger>
                <SelectContent>
                  {AFFILIATION_STATUS.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Year Established</Label>
              <Input
                type="number"
                {...form.register("year_established", { min: 0 })}
                placeholder="e.g., 1990"
                min={0}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </div>

          <div>
            <Label>Contact Person Name</Label>
            <Input
              {...form.register("contact_person")}
              placeholder="Enter contact person name"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                {...form.register("email")}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                {...form.register("phone", {
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit phone number",
                  },
                })}
                placeholder="Enter phone number"
                maxLength={10}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
              {/* {form.formState.errors.phone && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.phone.message)}
                </p>
              )} */}
            </div>
          </div>

          <div>
            <Label>Website</Label>
            <Input {...form.register("website")} placeholder="https://" />
          </div>

          <div>
            <Label>Images & Videos (multiple upload)</Label>
            <p className="mb-2 text-xs text-muted-foreground">
              Upload up to {maxInstitutionImages} images or videos of your
              institution (building, campus, etc.)
            </p>
            <div
              className={cn(
                "relative flex min-h-[120px] flex-col items-center justify-center rounded-lg border-2 border-dashed bg-muted/30 p-4 transition-colors",
                institutionImages.length >= maxInstitutionImages
                  ? "cursor-not-allowed border-muted-foreground/20 opacity-60"
                  : "border-muted-foreground/25 hover:border-muted-foreground/40 hover:bg-muted/50"
              )}
              onDragOver={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={e => {
                e.preventDefault();
                e.stopPropagation();
                if (institutionImages.length >= maxInstitutionImages) {
                  return;
                }
                const files = Array.from(e.dataTransfer.files).filter(
                  f =>
                    f.type.startsWith("image/") || f.type.startsWith("video/")
                );
                if (files.length) {
                  setInstitutionImages(prev =>
                    [...prev, ...files].slice(0, maxInstitutionImages)
                  );
                }
              }}
            >
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                disabled={institutionImages.length >= maxInstitutionImages}
                className="absolute inset-0 z-10 cursor-pointer opacity-0 disabled:pointer-events-none"
                onChange={e => {
                  if (institutionImages.length >= maxInstitutionImages) {
                    return;
                  }
                  const files = e.target.files
                    ? Array.from(e.target.files)
                    : [];
                  if (files.length) {
                    setInstitutionImages(prev =>
                      [...prev, ...files].slice(0, maxInstitutionImages)
                    );
                  }
                  e.target.value = "";
                }}
              />
              <Upload className="mb-2 h-8 w-8 text-muted-foreground" />
              <span className="text-center text-sm text-muted-foreground">
                {institutionImages.length >= maxInstitutionImages
                  ? `Maximum ${maxInstitutionImages} items reached`
                  : "Drag & drop images/videos here or click to browse"}
              </span>
              <span className="mt-1 text-xs text-muted-foreground">
                Maximum {maxInstitutionImages} images/videos
              </span>
            </div>
            {(existingImageUrls.length > 0 || institutionImages.length > 0) && (
              <div className="mt-3 flex flex-wrap gap-3">
                {existingImageUrls.map((url, index) => {
                  const isVideo =
                    url.toLowerCase().endsWith(".mp4") ||
                    url.toLowerCase().endsWith(".webm") ||
                    url.toLowerCase().endsWith(".ogg") ||
                    url.toLowerCase().endsWith(".mov") ||
                    url.toLowerCase().endsWith(".m4v");
                  return (
                    <div
                      key={url}
                      className="group relative flex flex-col items-center"
                    >
                      <div className="relative h-20 w-20 overflow-hidden rounded-lg border bg-muted">
                        {isVideo ? (
                          <video
                            src={url}
                            className="h-full w-full object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={url}
                            alt={`Institution ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                        )}
                        {onRemoveExistingImage && (
                          <button
                            type="button"
                            onClick={() => onRemoveExistingImage(index)}
                            className="text-destructive-foreground absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-destructive/90 opacity-0 transition-opacity group-hover:opacity-100"
                            aria-label="Remove item"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <span className="mt-1 max-w-[100px] truncate text-xs text-muted-foreground">
                        {isVideo ? "Video" : "Image"} {index + 1}
                      </span>
                    </div>
                  );
                })}
                {institutionImages.map((file, index) => {
                  const isVideo = file.type.startsWith("video/");
                  return (
                    <div
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      className="group relative flex flex-col items-center"
                    >
                      <div className="relative h-20 w-20 overflow-hidden rounded-lg border bg-muted">
                        {isVideo ? (
                          <video
                            src={URL.createObjectURL(file)}
                            className="h-full w-full object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="h-full w-full object-cover"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setInstitutionImages(prev =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="text-destructive-foreground absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-destructive/90 opacity-0 transition-opacity group-hover:opacity-100"
                          aria-label="Remove item"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="mt-1 max-w-[100px] truncate text-xs text-muted-foreground">
                        {file.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          {isModal && onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
              size="lg"
            >
              Cancel
            </Button>
          )}
          <Button type="submit" className="flex-1" size="lg">
            Continue
          </Button>
        </div>
      </form>
    );
  }

  if (step === 2) {
    return (
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">Address Information</h3>

          <div>
            <Label>Address Line 1</Label>
            <Input
              {...form.register("address_line1")}
              placeholder="Building name, street address"
            />
          </div>

          <div>
            <Label>Address Line 2</Label>
            <Input
              {...form.register("address_line2")}
              placeholder="Area, landmark"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>City</Label>
              <Input {...form.register("city")} placeholder="Enter city" />
            </div>
            <div>
              <Label>District</Label>
              <Input
                {...form.register("district")}
                placeholder="Enter district"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>State</Label>
              <Select
                onValueChange={(value: string) => form.setValue("state", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {INDIAN_STATES.map(state => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pincode</Label>
              <Input
                {...form.register("pincode", {
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: "Enter a valid 6-digit pincode",
                  },
                })}
                placeholder="Enter pincode"
                maxLength={6}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
              {form.formState.errors.pincode && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.pincode.message)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
            size="lg"
          >
            Back
          </Button>
          <Button type="submit" className="flex-1" size="lg">
            Continue
          </Button>
        </div>
      </form>
    );
  }

  if (step === 3) {
    return (
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">Academic Details</h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Board / University</Label>
              <Select
                onValueChange={(value: string) => form.setValue("board", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select board" />
                </SelectTrigger>
                <SelectContent>
                  {BOARDS.map(board => (
                    <SelectItem key={board.value} value={board.value}>
                      {board.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Medium of Instruction</Label>
              <div className="">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="h-auto min-h-[40px] w-full justify-between py-2"
                    >
                      <div className="flex flex-wrap gap-1">
                        {(form.watch("medium") ?? []).length > 0 ? (
                          (form.watch("medium") as string[]).map(val => {
                            const label = MEDIUM_OF_INSTRUCTION.find(
                              m => m.value === val
                            )?.label;
                            return (
                              <Badge
                                key={val}
                                variant="secondary"
                                className="mr-1"
                              >
                                {label}
                              </Badge>
                            );
                          })
                        ) : (
                          <span className="text-muted-foreground">
                            Select mediums...
                          </span>
                        )}
                      </div>
                      <Plus className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Search medium..." />
                      <CommandEmpty>No medium found.</CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-y-auto">
                        {MEDIUM_OF_INSTRUCTION.map(medium => {
                          const current = form.watch("medium") ?? [];
                          const selected = Array.isArray(current)
                            ? current.includes(medium.value)
                            : false;
                          return (
                            <CommandItem
                              key={medium.value}
                              onSelect={() => {
                                const next = selected
                                  ? current.filter(
                                      (v: string) => v !== medium.value
                                    )
                                  : [...current, medium.value];
                                form.setValue("medium", next);
                              }}
                              className="flex items-center gap-2"
                            >
                              <div
                                className={cn(
                                  "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                  selected
                                    ? "bg-primary text-primary-foreground"
                                    : "opacity-50 [&_svg]:invisible"
                                )}
                              >
                                <CheckCircle className={cn("h-3 w-3")} />
                              </div>
                              {medium.label}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <div>
            <Label>Accreditation</Label>
            <Select
              onValueChange={(value: string) =>
                form.setValue("accreditation", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select accreditation" />
              </SelectTrigger>
              <SelectContent>
                {ACCREDITATION.map(acc => (
                  <SelectItem key={acc.value} value={acc.value}>
                    {acc.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Education Levels</Label>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
              {INSTITUTION_LEVELS.map(level => (
                <div
                  key={level.value}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors",
                    selectedLevels.includes(level.value)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  onClick={() => toggleLevel(level.value)}
                >
                  <CheckCircle
                    className={
                      selectedLevels.includes(level.value)
                        ? "h-4 w-4 opacity-100"
                        : "h-4 w-4 opacity-0"
                    }
                  />
                  <span className="text-sm">{level.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Boarding Gender</Label>
            <p className="mb-2 text-xs text-muted-foreground">
              Select one or both (multiple values allowed). Sent as
              &quot;girls/boys&quot; when both selected.
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              {BOARDING_GENDER.map(opt => {
                const raw = form.watch("boarding_gender");
                const currentArr =
                  typeof raw === "string" && raw
                    ? raw.split("/").filter(Boolean)
                    : Array.isArray(raw)
                      ? raw
                      : [];
                const selected = currentArr.includes(opt.value);
                return (
                  <div
                    key={opt.value}
                    role="button"
                    tabIndex={0}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition-colors",
                      selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                    onClick={() => {
                      const next = selected
                        ? currentArr.filter(v => v !== opt.value)
                        : [...currentArr, opt.value];
                      form.setValue(
                        "boarding_gender",
                        next.length ? next.sort().join("/") : ""
                      );
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const next = selected
                          ? currentArr.filter(v => v !== opt.value)
                          : [...currentArr, opt.value];
                        form.setValue(
                          "boarding_gender",
                          next.length ? next.sort().join("/") : ""
                        );
                      }
                    }}
                  >
                    <CheckCircle
                      className={
                        selected ? "h-4 w-4 opacity-100" : "h-4 w-4 opacity-0"
                      }
                    />
                    <span className="text-sm">{opt.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Educational Year & Capacity</Label>
            <p className="mb-2 text-xs text-muted-foreground">
              Add one or more rows: educational year, total capacity, and
              vacant/occupied count.
            </p>
            <div className="mb-3 grid gap-3 rounded-lg border bg-muted/30 p-3 md:grid-cols-5">
              <Input
                placeholder="e.g. 2025-2026"
                value={educationalYearDraft.educational_year}
                onChange={e =>
                  setEducationalYearDraft(prev => ({
                    ...prev,
                    educational_year: e.target.value,
                  }))
                }
              />
              <Input
                type="number"
                min={0}
                placeholder="Total capacity"
                value={educationalYearDraft.total_capacity}
                onChange={e => {
                  const val = e.target.value;
                  const capacity = Number(val);
                  const occupied = Number(educationalYearDraft.occupied);
                  setEducationalYearDraft(prev => ({
                    ...prev,
                    total_capacity: val,
                    vacant:
                      !isNaN(capacity) && !isNaN(occupied)
                        ? String(Math.max(0, capacity - occupied))
                        : prev.vacant,
                  }));
                }}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
              <Input
                type="number"
                min={0}
                placeholder="Occupied"
                value={educationalYearDraft.occupied}
                onChange={e => {
                  const val = e.target.value;
                  const occupied = Number(val);
                  const capacity = Number(educationalYearDraft.total_capacity);
                  setEducationalYearDraft(prev => ({
                    ...prev,
                    occupied: val,
                    vacant:
                      !isNaN(capacity) && !isNaN(occupied)
                        ? String(Math.max(0, capacity - occupied))
                        : prev.vacant,
                  }));
                }}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
              <Input
                type="number"
                min={0}
                placeholder="Vacant"
                value={educationalYearDraft.vacant}
                onChange={e =>
                  setEducationalYearDraft(prev => ({
                    ...prev,
                    vacant: e.target.value,
                  }))
                }
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                className="gap-1"
                onClick={() => {
                  const year = educationalYearDraft.educational_year.trim();
                  const capacity = Number(educationalYearDraft.total_capacity);
                  const vacant = Number(educationalYearDraft.vacant);
                  const occupied = Number(educationalYearDraft.occupied);
                  if (!year) {
                    return;
                  }
                  const current: {
                    educational_year: string;
                    total_capacity: number;
                    vacant: number;
                    occupied: number;
                  }[] = form.getValues("educational_year") ?? [];
                  form.setValue("educational_year", [
                    ...current,
                    {
                      _id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                      educational_year: year,
                      total_capacity: Number.isNaN(capacity) ? 0 : capacity,
                      vacant: Number.isNaN(vacant) ? 0 : vacant,
                      occupied: Number.isNaN(occupied) ? 0 : occupied,
                    },
                  ]);
                  setEducationalYearDraft({
                    educational_year: "",
                    total_capacity: "",
                    vacant: "",
                    occupied: "",
                  });
                }}
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
            {(form.watch("educational_year") ?? []).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Added entries
                </p>
                <div className="overflow-hidden rounded-lg border bg-background">
                  <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 border-b bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
                    <div>Educational Year</div>
                    <div>Total Capacity</div>
                    <div>Occupied</div>
                    <div>Vacant</div>
                    <div className="w-10" />
                  </div>
                  {(form.watch("educational_year") ?? []).map(
                    (
                      row: {
                        _id?: string;
                        educational_year: string;
                        total_capacity: number;
                        occupied: number;
                        vacant: number;
                      },
                      index: number
                    ) => (
                      <div
                        key={
                          (row as { _id?: string })._id ??
                          `${row.educational_year}-${row.total_capacity}-${row.vacant}`
                        }
                        className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0"
                      >
                        <div className="font-medium">
                          {row.educational_year}
                        </div>
                        <div>{row.total_capacity}</div>
                        <div>{row.occupied}</div>
                        <div>{row.vacant}</div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-9 w-10 text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            const current =
                              form.getValues("educational_year") ?? [];
                            form.setValue(
                              "educational_year",
                              current.filter(
                                (_: unknown, i: number) => i !== index
                              )
                            );
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Affiliation Number</Label>
              <Input
                {...form.register("affiliation_number", {
                  pattern: {
                    value: /^[a-zA-Z0-9-]+$/,
                    message:
                      "Only alphanumeric characters and hyphens are allowed (e.g., INST-1234, ABC12345)",
                  },
                })}
                placeholder="Enter affiliation number"
              />
              {form.formState.errors.affiliation_number && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.affiliation_number.message)}
                </p>
              )}
            </div>
            <div>
              <Label>Recognition Number</Label>
              <Input
                {...form.register("recognition_number")}
                placeholder="Enter recognition number"
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
            size="lg"
          >
            Back
          </Button>
          <Button type="submit" className="flex-1" size="lg">
            Continue
          </Button>
        </div>
      </form>
    );
  }

  if (step === 4) {
    return (
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">Capacity & Facilities</h3>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Total Students</Label>
              <Input
                type="number"
                {...form.register("total_students", { min: 0 })}
                placeholder="Enter count"
                min={0}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div>
              <Label>Teaching Staff</Label>
              <Input
                type="number"
                {...form.register("teaching_staff", { min: 0 })}
                placeholder="Enter count"
                min={0}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div>
              <Label>Non-Teaching Staff</Label>
              <Input
                type="number"
                {...form.register("non_teaching_staff", { min: 0 })}
                placeholder="Enter count"
                min={0}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Number of Classrooms</Label>
              <Input
                type="number"
                {...form.register("classrooms", { min: 0 })}
                placeholder="Enter count"
                min={0}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div>
              <Label>Campus Area (in sq ft)</Label>
              <Input
                type="number"
                {...form.register("campus_area", { min: 0 })}
                placeholder="Enter area"
                min={0}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </div>

          <div>
            <Label>Departments / Faculties</Label>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
              {DEPARTMENTS.map(dept => (
                <div
                  key={dept}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors",
                    selectedDepartments.includes(dept)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  onClick={() => toggleDepartment(dept)}
                >
                  <CheckCircle
                    className={
                      selectedDepartments.includes(dept)
                        ? "h-4 w-4 opacity-100"
                        : "h-4 w-4 opacity-0"
                    }
                  />
                  <span className="text-sm">{dept}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Facilities Available</Label>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
              {FACILITIES.map(facility => (
                <div
                  key={facility}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors",
                    selectedFacilities.includes(facility)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  onClick={() => toggleFacility(facility)}
                >
                  <CheckCircle
                    className={
                      selectedFacilities.includes(facility)
                        ? "h-4 w-4 opacity-100"
                        : "h-4 w-4 opacity-0"
                    }
                  />
                  <span className="text-sm">{facility}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
            size="lg"
          >
            Back
          </Button>
          <Button type="submit" className="flex-1" size="lg">
            Continue
          </Button>
        </div>
      </form>
    );
  }

  if (step === 5) {
    return (
      <form
        onSubmit={form.handleSubmit(onFinalSubmit)}
        className="flex h-full flex-col space-y-4"
      >
        <div className="flex-1 space-y-4">
          <h3 className="mb-4 text-lg font-semibold">
            Trustee / Management Information
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Trustee / Principal Name</Label>
              <Input
                {...form.register("trustee_name")}
                placeholder="Enter name"
              />
            </div>
            <div>
              <Label>Designation</Label>
              <Select
                onValueChange={(value: string) =>
                  form.setValue("trustee_designation", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select designation" />
                </SelectTrigger>
                <SelectContent>
                  {TRUSTEE_DESIGNATIONS.map(designation => (
                    <SelectItem
                      key={designation.value}
                      value={designation.value}
                    >
                      {designation.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Trustee Phone</Label>
              <Input
                {...form.register("trustee_contact", {
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit phone number",
                  },
                })}
                placeholder="Enter phone number"
                maxLength={10}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
              {form.formState.errors.trustee_contact && (
                <p className="mt-1 text-xs text-destructive">
                  {String(form.formState.errors.trustee_contact.message)}
                </p>
              )}
            </div>
            <div>
              <Label>Trustee Email</Label>
              <Input
                type="email"
                {...form.register("trustee_email")}
                placeholder="Enter email"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Number of Trustees</Label>
              <Input
                type="number"
                {...form.register("trustee_count", { min: 0 })}
                placeholder="Enter count"
                min={0}
                onWheel={e => e.currentTarget.blur()}
                onKeyDown={e => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div>
              <Label>Trust Registration Number</Label>
              <Input
                {...form.register("trust_registration")}
                placeholder="Enter registration number"
              />
            </div>
          </div>

          <div>
            <Label>Names of Other Trustees</Label>
            <Textarea
              {...form.register("other_trustees")}
              placeholder="Enter names separated by comma"
              rows={2}
            />
          </div>

          <div>
            <Label>Additional Comments</Label>
            <Textarea
              {...form.register("comments")}
              placeholder="Any additional information about your institution..."
              rows={3}
            />
          </div>
        </div>

        <div className="sticky bottom-0 z-10 mt-auto flex gap-3 border-t bg-background py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
            size="lg"
          >
            Back
          </Button>
          <Button type="submit" className="flex-1" size="lg">
            {isEditMode ? "Update Institution" : "Submit Registration"}
          </Button>
        </div>
      </form>
    );
  }

  return null;
}
